import { FC, useEffect, useMemo } from 'react';
import { usePostHog } from '@posthog/react';
import { useTranslation } from 'react-i18next';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import ChevronRightIcon from '@/components/ui/icons/ChevronRightIcon.tsx';
import WhiteBlock from '@/components/ui/WhiteBlock/WhiteBlock.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import StarListElement from '@/components/ui/StarListElement/StarListElement.tsx';
import Separator from '@/components/ui/Separator/Separator.tsx';
import SubscriptionPlanBlock from '@/components/functional/SubscriptionPlanBlock/SubscriptionPlanBlock.tsx';
import Radio from '@/components/functional/RadioInput/Radio.tsx';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useProducts from '@/api/useProducts.ts';
import ProductType from '$/_const/ProductType.ts';
import { Alert, Button, Skeleton } from 'react-daisyui';
import SearchParams from '@/_constants/SearchParams.ts';
import { Link, useSearchParams } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import useUpgradeSubscription from '@/api/stripe/useUpgradeSubscription.ts';
import { UpgradeSubscriptionRequest, upgradeSubscriptionSchema } from '$/requests/UpgradeSubscription.ts';
import { queryClient } from '@/main.tsx';

const UpgradePage: FC = () => {
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { canUpgrade } = useAuthContext();
  const [searchParams] = useSearchParams();
  const redirectUrlParam = searchParams.get(SearchParams.REDIRECT_URL);
  const { control, handleSubmit, setValue } = useForm<UpgradeSubscriptionRequest>({
    resolver: zodResolver(upgradeSubscriptionSchema),
    defaultValues: {
      priceId: undefined,
    },
  });

  const {
    data: products,
    isLoading: isLoadingProducts,
    error: errorLoadingProducts,
  } = useProducts(true, {
    type: {
      comparator: 'in',
      value: [ProductType.SUBSCRIPTION],
    },
  });

  const {
    mutateAsync: upgradeSubscription,
    isPending: isLoadingUpgrade,
    error: errorUpgrade,
  } = useUpgradeSubscription();

  const redirectUrl = redirectUrlParam ? decodeURIComponent(redirectUrlParam) : null;

  const yearSubscription = useMemo(
    () => products?.find((productToFind) => productToFind.interval_count === 12 && productToFind.interval === 'month'),
    [products]
  );

  const monthSubscription = useMemo(
    () => products?.find((productToFind) => productToFind.interval_count === 1 && productToFind.interval === 'month'),
    [products]
  );

  // preselect year subscription if available
  useEffect(() => {
    if (yearSubscription) {
      setValue('priceId', yearSubscription.price_id);
    }
  }, [setValue, yearSubscription]);

  const handleUpgrade = (data: UpgradeSubscriptionRequest) => {
    void (async () => {
      try {
        if (isLoadingProducts || isLoadingUpgrade || !canUpgrade) return;

        await upgradeSubscription({
          priceId: data.priceId,
        });

        const selectedProduct = products?.find((p) => p.price_id === data.priceId);
        posthog?.capture('subscription_upgraded', {
          price_id: data.priceId,
          interval: selectedProduct?.interval,
          interval_count: selectedProduct?.interval_count,
          currency: selectedProduct?.currency,
        });

        if (redirectUrl) {
          setTimeout(async () => {
            await queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            window.location.href = redirectUrl;
          }, 4000); // wait for the subscription to be updated in the backend before redirecting
        }
      } catch (error) {
        console.error('Failed to upgrade subscription: ', error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <div className="w-[416px] md:h-1 md:grow overflow-auto max-w-full flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <span className="font-feature text-3.5xl font-bold italic">{t('subscriptionSelectPage.header')}</span>
          <span className="text-sm text-text-secondary whitespace-pre-line">
            {t('subscriptionSelectPage.subheader')}
          </span>
          <span className="text-sm text-primary italic">{t('subscriptionSelectPage.tagline')}</span>
        </div>
        {errorLoadingProducts ? <Alert status="error">{errorLoadingProducts.message}</Alert> : null}
        {errorUpgrade ? <Alert status="error">{errorUpgrade.message}</Alert> : null}
        <Separator />
        <div className="flex flex-col gap-4">
          <Controller
            control={control}
            name="priceId"
            render={({ field: { onChange, value }, formState: { errors } }) =>
              isLoadingProducts ? (
                <>
                  <Skeleton className="w-full h-24 rounded-xl" />
                  <Skeleton className="w-full h-24 rounded-xl" />
                </>
              ) : !yearSubscription && !monthSubscription ? (
                <p className="text-sm italic text-text-secondary text-center">{t('label.no_products_found')}</p>
              ) : (
                <Radio.Group value={value} onChange={onChange} error={errors.priceId?.message}>
                  {yearSubscription ? (
                    <Radio.Input
                      key={yearSubscription.id}
                      value={yearSubscription.price_id}
                      render={({ value, onChange, error, checked }) => {
                        const yearPricePerMonth = yearSubscription.price / 12;
                        const savePercentage = monthSubscription
                          ? 100 - Math.round((yearSubscription.price / (monthSubscription.price * 12)) * 100)
                          : undefined;

                        return (
                          <SubscriptionPlanBlock
                            currency={yearSubscription.currency}
                            perMonth={yearPricePerMonth}
                            perYear={yearSubscription.price}
                            save={savePercentage}
                            value={value}
                            error={!!error}
                            checked={checked}
                            onChange={onChange}
                            disabled={!canUpgrade}
                          />
                        );
                      }}
                    />
                  ) : null}
                  {monthSubscription ? (
                    <Radio.Input
                      key={monthSubscription.id}
                      value={monthSubscription.price_id}
                      render={({ value, onChange, error, checked }) => {
                        return (
                          <SubscriptionPlanBlock
                            currency={monthSubscription.currency}
                            perMonth={monthSubscription.price}
                            value={value}
                            error={!!error}
                            checked={checked}
                            onChange={onChange}
                            disabled
                          />
                        );
                      }}
                    />
                  ) : null}
                </Radio.Group>
              )
            }
          />
        </div>
        <Separator />
        <WhiteBlock.Wrapper>
          <StarListElement>{t('subscriptionSelectPage.feature1')}</StarListElement>
          <StarListElement>{t('subscriptionSelectPage.feature2')}</StarListElement>
          <StarListElement>{t('subscriptionSelectPage.feature3')}</StarListElement>
        </WhiteBlock.Wrapper>
        <Separator />
        <form className="flex flex-col" onSubmit={handleSubmit(handleUpgrade)}>
          <div className="flex flex-col gap-3">
            <LoadingButton
              disabled={isLoadingProducts || !canUpgrade}
              loading={isLoadingUpgrade}
              endIcon={<ChevronRightIcon className="w-3 h-3" />}
              type="submit"
              color="primary"
            >
              {t('button.upgrade')}
            </LoadingButton>
            <p className="text-text-secondary text-sm text-center">{t('subscriptionSelectPage.cancelNote')}</p>
          </div>
          <Link to={Routes.DASHBOARD} className="flex flex-col">
            <Button type="button" color="primary" variant="link" className="no-underline">
              {t('button.maybe_later')}
            </Button>
          </Link>
        </form>
      </div>
    </BrandCardSideContent>
  );
};

export default UpgradePage;
